import React, { useState, useRef } from 'react';
import { Loader2, Search, Upload, Globe, FileArchive } from 'lucide-react';
import { REGIONS, INDUSTRIES } from '../../types/scanner';

type ScanMode = 'url' | 'file';

interface ScanFormData {
  url: string;
  region: string;
  industry: string;
}

interface ScanFormProps {
  onSubmit: (data: ScanFormData) => Promise<void>;
  onFileSubmit?: (file: File, region: string, industry: string) => Promise<void>;
  isLoading?: boolean;
}

export const ScanForm: React.FC<ScanFormProps> = ({ onSubmit, onFileSubmit, isLoading }) => {
  const [mode, setMode] = useState<ScanMode>('url');
  const [url, setUrl] = useState('');
  const [region, setRegion] = useState('GLOBAL');
  const [industry, setIndustry] = useState('general');
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizeUrl = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed) return trimmed;
    // Add https:// if no protocol specified
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        setError('Please select a ZIP file');
        setSelectedFile(null);
        return;
      }
      setError(null);
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'url') {
      const normalizedUrl = normalizeUrl(url);

      // Basic URL validation
      try {
        new URL(normalizedUrl);
      } catch {
        setError('Please enter a valid URL');
        return;
      }

      await onSubmit({ url: normalizedUrl, region, industry });
    } else {
      // File mode
      if (!selectedFile) {
        setError('Please select a ZIP file');
        return;
      }

      if (!onFileSubmit) {
        setError('File scanning is not available');
        return;
      }

      await onFileSubmit(selectedFile, region, industry);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {/* Mode Toggle */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => { setMode('url'); setError(null); }}
              className={`btn btn-sm ${mode === 'url' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
            >
              <Globe size={16} />
              Scan URL
            </button>
            <button
              type="button"
              onClick={() => { setMode('file'); setError(null); }}
              className={`btn btn-sm ${mode === 'file' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
            >
              <FileArchive size={16} />
              Upload Files
            </button>
          </div>

          {mode === 'url' ? (
            <div className="form-field">
              <label htmlFor="url" className="form-label">Website URL</label>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <input
                  id="url"
                  type="text"
                  placeholder="example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className={`input ${error ? 'input-error' : ''}`}
                  style={{ flex: 1 }}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary btn-md"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Search size={18} />
                      Scan
                    </>
                  )}
                </button>
              </div>
              {error && <span className="form-error">{error}</span>}
            </div>
          ) : (
            <div className="form-field">
              <label htmlFor="file" className="form-label">Website ZIP File</label>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <input
                  ref={fileInputRef}
                  id="file"
                  type="file"
                  accept=".zip"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`input ${error ? 'input-error' : ''}`}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    cursor: 'pointer',
                    justifyContent: 'flex-start',
                    color: selectedFile ? 'var(--color-text)' : 'var(--color-muted)',
                  }}
                >
                  <Upload size={18} />
                  {selectedFile ? selectedFile.name : 'Click to select a ZIP file...'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !selectedFile}
                  className="btn btn-primary btn-md"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Search size={18} />
                      Scan
                    </>
                  )}
                </button>
              </div>
              {error && <span className="form-error">{error}</span>}
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-muted)', marginTop: 'var(--space-2)' }}>
                Upload a ZIP file containing your website files (HTML, CSS, JS). URL-dependent checks will be skipped.
              </p>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-field">
              <label htmlFor="region" className="form-label">Region</label>
              <div className="select-wrapper">
                <select
                  id="region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className="select"
                >
                  {REGIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <div className="select-arrow" />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="industry" className="form-label">Industry</label>
              <div className="select-wrapper">
                <select
                  id="industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="select"
                >
                  {INDUSTRIES.map((i) => (
                    <option key={i.value} value={i.value}>{i.label}</option>
                  ))}
                </select>
                <div className="select-arrow" />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScanForm;
